<template>
  <div>
    <!-- Content Container -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Mobile View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div 
          v-for="order in (orders?.data || [])" 
          :key="order.id" 
          class="p-4 hover:bg-gray-50 transition-colors" 
          :class="{'bg-red-50/50': order.order_state === 'cancel'}"
        >
          <Link :href="`/orders/${order.id}`" class="block space-y-3">
            <div class="flex justify-between items-start gap-2">
              <span class="font-bold text-gray-900 text-sm">#{{ order.number_order || order.id }}</span>
              <span
                :class="[
                  'px-2 py-0.5 rounded text-xs font-bold shrink-0',
                  getStateClass(order.order_state)
                ]"
              >
                {{ getStateName(order.order_state) }}
              </span>
            </div>
            
            <div class="text-sm text-gray-600 space-y-1">
              <p class="font-medium text-gray-800">{{ order.client?.name || '-' }}</p>
              <p class="text-xs text-gray-500">{{ order.date }}</p>
              <p v-if="order.comentary" class="italic text-gray-500 text-xs">"{{ order.comentary }}"</p>
            </div>

            <div class="flex justify-between items-center pt-2 border-t border-gray-100 mt-2">
              <div class="flex items-center gap-1.5 text-xs text-gray-500">
                <i class="fa fa-user text-gray-400 text-xs" />
                <span>{{ order.responsible ? `${order.responsible.first_name} ${order.responsible.last_name}` : 'Directo' }}</span>
              </div>
              <span class="font-bold text-gray-900 text-base">{{ formatCurrency(order.total) }}</span>
            </div>
          </Link>
        </div>

        <!-- Mobile Empty State -->
        <div v-if="!orders || (orders.data && orders.data.length === 0)" class="p-8 text-center text-gray-400">
          <i class="fa fa-receipt text-4xl mb-3 text-gray-300" />
          <h3 class="text-base font-bold text-gray-800">No hay ventas registradas</h3>
          <p class="text-xs text-gray-500 mt-1">Las ventas realizadas aparecerán aquí.</p>
        </div>
      </div>

      <!-- Desktop View -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="bg-gray-50/70 text-left border-b border-gray-100">
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">#</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Fecha</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Estado</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider max-w-xs">Observación</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Cliente</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Vendedor</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Total</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="order in (orders?.data || [])" 
              :key="order.id" 
              class="group hover:bg-gray-50 transition-colors"
              :class="{'bg-red-50/50 hover:bg-red-100/50': order.order_state === 'cancel'}"
            >
              <td class="px-6 py-4">
                <Link :href="`/orders/${order.id}`" class="font-medium text-blue-600 hover:text-blue-700 text-sm">
                  {{ order.number_order || order.id }}
                </Link>
              </td>
              
              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ order.date }}
              </td>

              <td class="px-6 py-4">
                <span
                  :class="[
                    'px-2.5 py-1 rounded text-xs font-bold inline-block',
                    getStateClass(order.order_state)
                  ]"
                >
                  {{ getStateName(order.order_state) }}
                </span>
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm max-w-xs truncate" :title="order.comentary">
                {{ order.comentary || '-' }}
              </td>

              <td class="px-6 py-4 text-gray-800 font-medium text-sm">
                {{ order.client?.name || '-' }}
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ order.responsible ? `${order.responsible.first_name} ${order.responsible.last_name}` : 'Transacción directa' }}
              </td>

              <td class="px-6 py-4 text-right font-bold text-gray-900 text-sm">
                {{ formatCurrency(order.total) }}
              </td>
            </tr>

            <!-- Desktop Empty State -->
            <tr v-if="!orders || (orders.data && orders.data.length === 0)">
              <td colspan="7" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i class="fa fa-receipt text-2xl opacity-50" />
                  </div>
                  <h3 class="text-base font-bold text-gray-900 mb-1">No hay ventas registradas</h3>
                  <p class="text-gray-500 text-sm max-w-sm">
                    Las ventas realizadas aparecerán aquí.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="orders?.links" class="mt-6 flex justify-center">
      <pagination :links="orders.links" />
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Pagination from '@/Shared/Pagination'

export default {
  name: 'OrderTable',

  components: {
    Link,
    Pagination,
  },

  props: {
    orders: {
      type: Object,
      required: true,
    },
    states: {
      type: Array,
      default: () => [],
    },
  },

  methods: {
    getStateName(state) {
      const stateMap = {
        'request': 'Pendiente por pago',
        'Pendiente': 'Pendiente por pago',
        'pendiente_pago': 'Pendiente por pago',
        'por_iniciar': 'Por iniciar',
        'en_preparacion': 'En preparación',
        'estimate': 'Presupuesto',
        'pay': 'Pagada',
        'pagado': 'Pagada',
        'payRevition': 'Pago en revisión',
        'dispatch': 'Despachada',
        'Despachado': 'Despachada',
        'delivery': 'En espera de despacho',
        'Entregado': 'Entregada',
        'finish': 'Finalizada',
        'finalizado': 'Finalizada',
        'cancel': 'Anulada',
      }
      return stateMap[state] || state
    },

    getStateClass(state) {
      switch(state) {
      case 'estimate': return 'bg-yellow-100 text-yellow-800'
      case 'cancel': return 'bg-red-100 text-red-800'
      case 'request':
      case 'Pendiente':
      case 'pendiente_pago':
      case 'por_iniciar': return 'bg-amber-100 text-amber-800'
      case 'finish':
      case 'finalizado': return 'bg-emerald-100 text-emerald-800'
      case 'pay':
      case 'pagado': return 'bg-green-100 text-green-800'
      case 'payRevition': return 'bg-purple-100 text-purple-800'
      case 'dispatch':
      case 'Despachado': return 'bg-indigo-100 text-indigo-800'
      case 'delivery':
      case 'Entregado': return 'bg-orange-100 text-orange-800'
      case 'en_preparacion': return 'bg-blue-100 text-blue-800 border border-blue-200'
      default: return 'bg-gray-100 text-gray-800'
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0)
    },
  },
}
</script>
